import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Send, CheckCircle2, GraduationCap } from "lucide-react";
const EMPTY = { name: "", email: "", phone: "", facility: "", location: "", specialty: "", experience: "", message: "" };

export const CoachSignupForm = ({ compact = false }) => {
  const { user } = useAuth();
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!user) return;
    setForm((f) => ({ ...f, name: f.name || user.name || "", email: f.email || user.email || "",
      phone: f.phone || user.phone || "", location: f.location || user.location || "" }));
  }, [user]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/coaches/apply", form);
      setDone(true);
      toast.success("Application received — we'll reach out soon!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not submit application");
    } finally { setBusy(false); }
  };

  if (done) {
    return (
      <div className="border border-pb-red/40 bg-pb-red/5 p-6 text-center" data-testid="coach-apply-success">
        <CheckCircle2 className="w-10 h-10 text-pb-red mx-auto mb-3" />
        <div className="font-display font-black uppercase text-xl mb-1">Application Received</div>
        <p className="text-sm text-zinc-400">Thanks {form.name.split(" ")[0]}! We'll review your application and reach out at {form.phone} to get you set up.</p>
      </div>
    );
  }

  const input = "w-full bg-pb-elevated border border-pb-border px-3 py-2.5 text-white placeholder-zinc-600 focus:outline-none focus:border-pb-red text-sm";
  const lbl = "block text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500 mb-1.5";

  return (
    <form onSubmit={submit} className="space-y-3" data-testid="coach-apply-form">
      {!compact && (
        <div className="flex items-center gap-2 mb-1">
          <GraduationCap className="w-5 h-5 text-pb-red" />
          <span className="font-display font-bold text-lg uppercase">Coach Application</span>
        </div>
      )}
      <div className="grid sm:grid-cols-2 gap-3">
        <div><label className={lbl}>Full Name *</label>
          <input data-testid="coach-apply-name" required value={form.name} onChange={set("name")} placeholder="Your name" className={input} /></div>
        <div><label className={lbl}>Email *</label>
          <input data-testid="coach-apply-email" required type="email" value={form.email} onChange={set("email")} placeholder="you@email.com" className={input} /></div>
        <div><label className={lbl}>Phone *</label>
          <input data-testid="coach-apply-phone" required value={form.phone} onChange={set("phone")} placeholder="941-555-0000" className={input} /></div>
        <div><label className={lbl}>Facility / Club</label>
          <input data-testid="coach-apply-facility" value={form.facility} onChange={set("facility")} placeholder="Your facility name" className={input} /></div>
        <div><label className={lbl}>Location</label>
          <input data-testid="coach-apply-location" value={form.location} onChange={set("location")} placeholder="City, State" className={input} /></div>
        <div><label className={lbl}>Specialty</label>
          <input data-testid="coach-apply-specialty" value={form.specialty} onChange={set("specialty")} placeholder="e.g. Dinks, Strategy, Low Intermediates" className={input} /></div>
      </div>
      <div><label className={lbl}>Coaching Experience</label>
        <input data-testid="coach-apply-experience" value={form.experience} onChange={set("experience")} placeholder="e.g. 5 years, PPR certified" className={input} /></div>
      <div><label className={lbl}>Anything else?</label>
        <textarea data-testid="coach-apply-message" value={form.message} onChange={set("message")} rows={2}
          placeholder="Tell us about you and your goals…" className={`${input} resize-none`} /></div>
      <button type="submit" data-testid="coach-apply-submit" disabled={busy}
        className="w-full bg-pb-red text-black font-bold uppercase tracking-widest py-3 hover:bg-[#BEF264] active:scale-[0.98] transition-all disabled:opacity-50 flex items-center justify-center gap-2">
        <Send className="w-4 h-4" /> {busy ? "Submitting…" : "Apply to Coach"}
      </button>
    </form>
  );
};

export default CoachSignupForm;
